import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ScreenTitle } from "@/components/ScreenTitle";
import { useSite } from "@/context/SiteContext";
import { Plus, Power, PowerOff, Trash2, Play, ChevronDown, ChevronUp, Zap } from "lucide-react";

// Restricted list — only these 3 authors are ever assignable to a feed.
const AUTHORS = [
  { id: 1,  name: "Service Actualités",             short: "Actualités",  color: "bg-[var(--accent)] text-white" },
  { id: 10, name: "Service politique",              short: "Politique",   color: "bg-[#3f6e4a] text-white" },
  { id: 18, name: "Service actualité internationale", short: "International", color: "bg-[#2a5788] text-white" },
];
const authorById = (id) => AUTHORS.find((a) => a.id === id) || null;

export default function FeedsPage() {
  const { currentId: siteId } = useSite();
  const [feeds, setFeeds] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [running, setRunning] = useState(null);
  const [form, setForm] = useState({
    name: "", url: "", wp_category_id: "", wp_category_name: "",
    active: true, language: "en", wp_author_id: 1,
  });

  const load = async () => {
    const r = await api.get("/feeds", { params: siteId ? { site_id: siteId } : {} });
    setFeeds(r.data);
  };
  useEffect(() => { load(); }, [siteId]);   // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/feeds", {
        ...form,
        wp_category_id: form.wp_category_id ? parseInt(form.wp_category_id, 10) : null,
        wp_category_name: form.wp_category_name || null,
        wp_author_id: form.wp_author_id || 1,
        site_ids: siteId ? [siteId] : [],
      });
      toast.success("Flux validé et ajouté");
      setForm({ name: "", url: "", wp_category_id: "", wp_category_name: "",
                active: true, language: "en", wp_author_id: 1 });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'ajouter ce flux");
    }
  };

  const toggle = async (f) => {
    await api.patch(`/feeds/${f.id}`, { active: !f.active });
    load();
  };
  const switchLang = async (f, lang) => {
    await api.patch(`/feeds/${f.id}`, { language: lang });
    load();
  };
  const setAuthor = async (f, authorId) => {
    await api.patch(`/feeds/${f.id}`, { wp_author_id: authorId });
    toast.success(`Auteur : ${authorById(authorId)?.name}`);
    load();
  };
  const editCategory = async (f) => {
    const name = window.prompt("Nom de la catégorie WordPress", f.wp_category_name || "");
    if (name === null) return;
    const idStr = window.prompt("ID de la catégorie (facultatif)", f.wp_category_id ? String(f.wp_category_id) : "");
    if (idStr === null) return;
    await api.patch(`/feeds/${f.id}`, {
      wp_category_name: name || null,
      wp_category_id: idStr ? parseInt(idStr, 10) : null,
    });
    load();
  };
  const remove = async (f) => {
    if (!window.confirm(`Supprimer « ${f.name} » ?`)) return;
    await api.delete(`/feeds/${f.id}`);
    toast.success("Flux supprimé");
    load();
  };
  const runManual = async (f) => {
    setRunning(f.id);
    try {
      await api.post("/process/test", { feed_id: f.id });
      toast.success("Brouillon créé — voir la File");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Traitement échoué");
    } finally {
      setRunning(null);
    }
  };

  const generateFromFeed = async (f) => {
    const raw = window.prompt(
      `Générer combien d'articles depuis « ${f.name} » ? (1 à 25)`,
      "5",
    );
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (!Number.isInteger(n) || n < 1 || n > 25) {
      toast.error("Nombre invalide (1 à 25)");
      return;
    }
    setRunning(f.id);
    try {
      const r = await api.post(`/process/refresh-feed/${f.id}?max_items=${n}`,
        null, { params: siteId ? { site_id: siteId } : {} });
      toast.success(`Génération lancée sur « ${r.data.feed_name} » (cap ${n}). Voir la File d'ici 1 min.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération impossible");
    } finally {
      setRunning(null);
    }
  };

  return (
    <div data-testid="feeds-page">
      <ScreenTitle
        eyebrow={`Sources · ${feeds.length}`}
        title="Flux"
        right={
          <button
            onClick={() => setShowAdd((v) => !v)}
            data-testid="feed-toggle-add"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-black hover:bg-black hover:text-white transition-colors"
            aria-label="Ajouter un flux"
          >
            {showAdd ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
        }
      />

      {showAdd && (
        <form
          onSubmit={submit}
          data-testid="feed-create-form"
          className="px-5 py-4 border-t border-b border-[var(--border-default)] bg-white space-y-3"
        >
          <Field label="Nom">
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="feed-name-input"
              placeholder="Reuters World News"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <Field label="URL du flux">
            <input
              required
              type="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              data-testid="feed-url-input"
              placeholder="https://…/rss.xml"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Langue de la source">
              <select
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                data-testid="feed-lang-select"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none bg-white focus:outline-none focus:border-black"
              >
                <option value="fr">Français (réécriture seule)</option>
                <option value="en">Anglais (traduction + réécriture)</option>
              </select>
            </Field>
            <Field label="Cat. WP · libellé">
              <input
                value={form.wp_category_name}
                onChange={(e) => setForm({ ...form, wp_category_name: e.target.value })}
                data-testid="feed-catname-input"
                placeholder="ex : Monde"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
          </div>
          <Field label="Cat. WP · ID (optionnel)">
            <input
              type="number"
              value={form.wp_category_id}
              onChange={(e) => setForm({ ...form, wp_category_id: e.target.value })}
              data-testid="feed-catid-input"
              placeholder="ex : 12"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <Field label="Service auteur (obligatoire)">
            <div className="grid grid-cols-3 gap-2" data-testid="feed-author-picker">
              {AUTHORS.map((a) => {
                const active = form.wp_author_id === a.id;
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => setForm({ ...form, wp_author_id: a.id })}
                    data-testid={`feed-author-choice-${a.id}`}
                    className={`min-h-[52px] px-2 py-1.5 border text-left transition-colors ${
                      active
                        ? `${a.color} border-black`
                        : "bg-white border-[var(--border-default)] hover:border-black"
                    }`}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-80">
                      {active ? "sélectionné" : "choisir"}
                    </div>
                    <div className="text-[12px] font-semibold leading-tight">{a.short}</div>
                  </button>
                );
              })}
            </div>
          </Field>
          <button
            type="submit"
            data-testid="feed-add-submit"
            className="w-full min-h-[48px] bg-black text-white text-sm uppercase tracking-widest font-medium hover:bg-[#222] transition-colors"
          >
            Ajouter
          </button>
        </form>
      )}

      <div data-testid="feeds-list">
        {feeds.length === 0 && (
          <div className="px-5 py-16 text-center text-sm text-[var(--text-muted)]">
            Aucun flux. Ajoute-en un pour démarrer.
          </div>
        )}
        {feeds.map((f) => {
          const authorObj = authorById(f.wp_author_id);
          return (
          <div
            key={f.id}
            className="px-5 py-4 border-b border-[var(--border-default)] last:border-b-0"
            data-testid={`feed-row-${f.id}`}
          >
            <div className="flex items-start gap-3">
              <span
                className={`mt-2 inline-block w-2 h-2 rounded-full ${f.active ? "bg-[var(--status-success)]" : "bg-[var(--text-muted)]"}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2">
                  <div className="font-headings text-lg leading-tight truncate flex-1">{f.name}</div>
                  {authorObj ? (
                    <span
                      className={`shrink-0 px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${authorObj.color}`}
                      data-testid={`feed-author-badge-${f.id}`}
                      title={authorObj.name}
                    >
                      {authorObj.short}
                    </span>
                  ) : (
                    <span
                      className="shrink-0 px-2 py-1 text-[10px] font-mono uppercase tracking-wider bg-[var(--accent-strong)] text-white"
                      data-testid={`feed-author-badge-${f.id}`}
                      title="Aucun service auteur — attribué par défaut à Service Actualités"
                    >
                      auteur ?
                    </span>
                  )}
                </div>
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] font-mono text-[var(--text-muted)] truncate block mt-0.5 hover:underline"
                >
                  {f.url}
                </a>
                <div className="text-[11px] font-mono text-[var(--text-secondary)] mt-1.5 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => switchLang(f, f.language === "fr" ? "en" : "fr")}
                    data-testid={`feed-lang-${f.id}`}
                    className="px-1.5 py-0.5 border border-[var(--border-default)] hover:border-black uppercase tracking-wider"
                    aria-label="Basculer la langue"
                    title="Basculer FR/EN"
                  >
                    {f.language === "fr" ? "FR · réécriture" : "EN · trad + réécriture"}
                  </button>
                  <button
                    onClick={() => editCategory(f)}
                    data-testid={`feed-cat-${f.id}`}
                    className="hover:underline"
                  >
                    {f.wp_category_id || f.wp_category_name ? (
                      <>cat. {f.wp_category_name || `#${f.wp_category_id}`}</>
                    ) : (
                      <span className="text-[var(--text-muted)]">+ catégorie WP</span>
                    )}
                  </button>
                  {f.last_checked_at && (
                    <span>· vu {new Date(f.last_checked_at).toLocaleTimeString("fr-FR")}</span>
                  )}
                </div>

                {/* Inline author picker — 3 options only, current one visibly selected */}
                <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid={`feed-author-inline-${f.id}`}>
                  {AUTHORS.map((a) => {
                    const active = f.wp_author_id === a.id;
                    return (
                      <button
                        key={a.id}
                        onClick={() => setAuthor(f, a.id)}
                        data-testid={`feed-author-set-${f.id}-${a.id}`}
                        className={`min-h-[36px] px-2 text-[11px] uppercase tracking-wider font-mono border transition-colors flex items-center justify-center gap-1 ${
                          active
                            ? `${a.color} border-black`
                            : "bg-white border-[var(--border-default)] text-[var(--text-secondary)] hover:border-black hover:text-black"
                        }`}
                        aria-pressed={active}
                        title={a.name}
                      >
                        {active && <span aria-hidden>✓</span>}
                        <span className="truncate">{a.short}</span>
                      </button>
                    );
                  })}
                </div>

                {f.last_error && (
                  <div className="mt-1 text-[11px] font-mono text-[var(--accent-strong)] truncate">
                    ⚠ {f.last_error}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                onClick={() => generateFromFeed(f)}
                disabled={running === f.id || !f.active}
                data-testid={`feed-generate-${f.id}`}
                title="Générer plusieurs articles depuis ce flux (choisir combien)"
                className="min-h-[44px] px-3 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] transition-colors flex items-center gap-1.5 disabled:opacity-60"
              >
                <Zap className="w-3.5 h-3.5" strokeWidth={1.75} />
                Générer N…
              </button>
              <button
                onClick={() => runManual(f)}
                disabled={running === f.id || !f.active}
                data-testid={`feed-run-${f.id}`}
                title="Traiter 1 seul item (test rapide)"
                className="min-h-[44px] px-3 border border-black text-[11px] uppercase tracking-widest font-mono hover:bg-black hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-60"
              >
                <Play className="w-3.5 h-3.5" strokeWidth={1.75} />
                {running === f.id ? "…" : "1 item"}
              </button>
              <button
                onClick={() => toggle(f)}
                data-testid={`feed-toggle-${f.id}`}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-[var(--border-default)] hover:border-black transition-colors"
                aria-label={f.active ? "Désactiver" : "Activer"}
              >
                {f.active ? <PowerOff className="w-4 h-4" strokeWidth={1.75} /> : <Power className="w-4 h-4" strokeWidth={1.75} />}
              </button>
              <button
                onClick={() => remove(f)}
                data-testid={`feed-delete-${f.id}`}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-[var(--border-default)] hover:border-[var(--accent-strong)] hover:text-[var(--accent-strong)] transition-colors"
                aria-label="Supprimer"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        );})}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="caption mb-1 block">{label}</label>
      {children}
    </div>
  );
}
